import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';

import { fuseAnimations } from '@fuse/animations';
import { Referral } from 'app/shared/models/referral';
import { Subject, SubjectOrganisation, SubjectReferral } from 'app/shared/models/subject';
import { SubjectTypeService } from 'app/shared/services/subject-type.service';
import { take } from 'rxjs/operators';
import { DefaultDataService } from 'app/shared/services/default-data.service';
import { ReferralSource } from 'app/shared/models/referral-source';
import { ReferralSourceService } from 'app/shared/services/referral-source.service';
import { SubjectSettingService } from 'app/shared/services/subject-setting.service';
import { User } from 'app/shared/models/user';
import { UserService } from 'app/shared/services/user.service';
import { Subscription } from 'rxjs';
import { SubjectService } from 'app/shared/services/subject.service';
import { Layout } from 'app/shared/models/layout';
import { AuthService } from 'app/shared/services/auth.service';
import { environment } from 'environments/environment';

@Component({
  selector: 'referral-dialog',
  templateUrl: './referral-dialog.component.html',
  styleUrls: ['./referral-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: fuseAnimations,
})
export class ReferralDialogComponent implements OnInit, OnDestroy {
  title = 'Add Referral';
  layout: Layout;
  referral: Referral;
  form: UntypedFormGroup;
  referralSources: ReferralSource[];
  autoGenerateReference = false;
  referralUsers: User[] = [];
  onReferralUsers: Subscription;
  subject = {
    layout: undefined,
    subject: undefined,
    changedSubject: undefined,
    canSave: false,
    sizeY: 522,
    sizeX: 630,
    subjectType: undefined,
  };

  constructor(
    private defaultDataService: DefaultDataService,
    private referralSourceService: ReferralSourceService,
    private subjectService: SubjectService,
    private subjectTypeService: SubjectTypeService,
    private subjectSettingService: SubjectSettingService,
    private userService: UserService,
    public dialogRef: MatDialogRef<ReferralDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit() {
    this.subjectTypeService
      .getById(this.defaultDataService.referralPrimarySubjectTypeId)
      .pipe(take(1))
      .subscribe((subjectType) => {
        const subject = new Subject();
        subject.isReferral = true;
        subject.subjectType = subjectType;
        subject.subjectTypeId = subjectType.id;

        if (environment.limitAccessToSubjectsByOrg===true)
          subject.subjectOrganisations?.push(new SubjectOrganisation({ organisationId :this.userService.getCurrentUser()?.organisationId}));
        
        this.subject.subject = subject;
        this.subject.subjectType = subjectType;
        
        this.onReferralUsers = this.userService
          .getActiveUsers()
          .subscribe((users) => {
            this.referralUsers = users;
          });

        // get the subject settings so we can check for autogenerate when building the form
        this.subjectSettingService
          .getBySubjectTypeId(subjectType.id)
          .subscribe((setting) => {
            this.autoGenerateReference =
              setting && setting.autoGenerateReference;
          });
      });

    this.form = this.formBuilder.group({
      referralSourceId: [''],
      // referralUserId: [],
      referrerName: ['', [Validators.required]],
      referrerTelephone: [''],
      referrerEmail: ['', [Validators.required]],
      managerName: ['', [Validators.required]],
      managerTelephone: [''],
      managerEmail: ['', [Validators.required]],
    });

    this.referralSourceService
      .getAllData()
      .pipe(take(1))
      .subscribe((referralSources: ReferralSource[]) => {
        if (referralSources.length) {
          this.referralSources = referralSources
            .filter((i) => !i.hideRecord)
            .sort((a, b) => a.name.localeCompare(b.name));
          // #5028 - hide Search Referral referral source unless marker search and referral is enabled
          if (!this.defaultDataService.enableMarkerSearch) {
            this.referralSources = this.referralSources.filter(
              (i) => i.name !== 'Search Referral'
            );
          }
          if (this.referralSources.length) {
            this.form.patchValue({
              referralSourceId: this.referralSources[0].id,
            });
          }
        }
      });
  }

  ngOnDestroy() {
    this.onReferralUsers?.unsubscribe();
  }

  subjectChanged(data) {
    if (this.subject && data && data.subject) {
      this.subject.canSave = data.valid;
      this.subject.changedSubject = data.subject;
    }
  }

  layoutChanged(layout) {
    if (layout && this.subject) {
      this.subject.layout = layout.layout;
      this.subject.sizeY = layout.sizeY;
      this.subject.sizeX = layout.sizeX;
    }
  }

  save() {
    // create and populate the referral
    const val = this.form.getRawValue();
    const referral = new Referral(val);
    // const userId = this.form.controls['referralUserId'].value;

    // set the summary
    this.subjectService
      .hydrate(this.subject.changedSubject)
      .pipe(take(1))
      .subscribe((hydratedSubject) => {
        const subject = new Subject(hydratedSubject);
        subject.subjectTypeId = subject.subjectTypeId || subject.subjectType.id;

        subject.subjectType = null;
        if (this.subject.layout) {
          subject.summary = subject.getSummary(this.subject.layout, true);
        }
        referral.summary = subject.summary;

        // link the referral to the subject...
        subject.subjectReferrals = subject.subjectReferrals || [];
        subject.subjectReferrals.push(new SubjectReferral({ referral }));

        // set referral flags
        subject.isReferral = true;
        subject.hasReferral = true;

        // var su: SubjectUser = new SubjectUser();
        // su.userId = userId;
        // subject.subjectUsers.push(su);

        this.subjectService.onAfterSave
          .pipe(take(1))
          .subscribe((savedReferralSubject) => {
            if (savedReferralSubject) {
              if (
                this.autoGenerateReference &&
                savedReferralSubject.id !== subject.id
              ) {
                const s = new Subject(savedReferralSubject);
                s.summary = s.getSummary(this.subject.layout, true);
                if (s.subjectReferrals && s.subjectReferrals.length > 0) {
                  s.subjectReferrals[0].referral.summary = s.summary;
                }
                this.subjectService.save(s, null, false);
              }
              this.dialogRef.close({
                subject: savedReferralSubject,
              });
            }
          });

        // ...and save the subject
        this.subjectService.save(subject, 'Referral saved');
      });
  }
}
